/** @jsxImportSource theme-ui */

import { useBreakpointIndex } from '@theme-ui/match-media';
import { Formik, FormikHelpers } from 'formik';
import { NumericDictionary } from 'lodash';
import metadataSelectors from 'metadata/metadata.selectors';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, DropdownItemProps, Grid } from 'semantic-ui-react';
import { SxProp } from 'theme-ui';
import * as Yup from 'yup';

import * as dto from 'client/dto';
import { hasIntersection, mapToDictionary } from 'utils/arrayHelpers';
import { DefaultMaxUserLocations } from 'utils/constants';
import { sxMixinActiveHover, sxtheme } from 'utils/cssHelpers';
import { isMobile, isNonMobile } from 'utils/responsive';
import { formatNumber } from 'utils/stringFormattingHelpers';
import { nameof } from 'utils/typeHelpers';

import { FlexGroup } from 'components/_common/FlexGroup';
import { BdParagraph } from 'components/_common/SectionHeader';
import { Typography } from 'components/_common/Typography';

import { LocationMultiSelectFormikControl } from 'locations/components/LocationMultiSelectFormikControl';
import { LocationSelectionItem } from 'locations/components/LocationsModel';

import SettingsSubmitButton from '../_common/SettingsSubmitButton';
import { FormikSemanticDropDown } from '../FormikControls/FormikSemanticDropDown';
import {
  CustomSettingsFM,
  CustomSettingsSubmitData,
  DiseaseNotificationLevelDict,
  RoleAndItsPresets,
} from './CustomSettingsModels';
import { DiseaseRelevanceMatrixFormikControl } from './FormikControl.DiseaseRelevanceMatrix';

// TODO: 22b9ce42: move to SettingsView/_common
export const PageHeading: React.FC = ({ children }) => (
  <div
    sx={{
      borderBottom: sxtheme(t => `1px solid ${t.colors.deepSea100}`),
      pt: '16px',
      pb: '10px',
      mt: '0',
      mb: '20px',
    }}
  >
    <Typography variant="h5" color="deepSea100" marginBottom="0">
      {children}
    </Typography>
  </div>
);

export const FlexFormRow: React.FC = ({ children }) => (
  <div sx={{ display: 'flex', flexFlow: 'row wrap', margin: '0 -15px' }}>{children}</div>
);

export const FormFieldGroup: React.FC<SxProp> = props => (
  <div
    sx={{
      display: 'flex',
      flex: '1 0 320px',
      flexDirection: 'column',
      padding: '0 15px',
    }}
    {...props}
  >
    {props.children}
  </div>
);

export const InputErrorMessage: React.FC = ({ children }) => (
  <div className="invalid-feedback">
    <Typography sx={{ display: 'inline-block', pt: '6px' }} variant="caption" color="starfish100">
      {children}
    </Typography>
  </div>
);

const PageParagraph: React.FC = ({ children }) => (
  <BdParagraph>
    <Typography variant="body1" color="stone90">
      {children}
    </Typography>
  </BdParagraph>
);

// =====================================================================================================================================

type CustomSettingsFormProps = {
  rolesAndPresets: RoleAndItsPresets[];
  diseases: NumericDictionary<dto.DiseaseInformationModel>;
  diseaseGroupings: dto.DiseaseGroupModel[];
  geonames: dto.GetGeonameModel[];
  userRoleIdInitial: string;
  userDiseaseRelevanceSetting: DiseaseNotificationLevelDict;
  onSubmit: (
    data: CustomSettingsSubmitData,
    setSubmitting: (isSubmitting: boolean) => void
  ) => void;
};

// =====================================================================================================================================

export const CustomSettingsForm: React.FC<CustomSettingsFormProps> = ({
  rolesAndPresets,
  diseases,
  diseaseGroupings,
  geonames,
  userRoleIdInitial,
  userDiseaseRelevanceSetting,
  onSubmit,
}) => {
  const appMetadata = useSelector(metadataSelectors.getMetadata);
  const refCustomDiseasesButtonDiv = React.useRef<HTMLDivElement>();
  const [isCustomizeDiseasesOpen, setIsCustomizeDiseasesOpen] = useState(false);

  const isNonMobileDevice = isNonMobile(useBreakpointIndex());
  const isMobileDevice = isMobile(useBreakpointIndex());
  const maxAois = appMetadata?.maxUserLocations ?? DefaultMaxUserLocations;

  function Map2SubmitData(
    values: CustomSettingsFM,
    optimize: 'quick' | 'full'
  ): CustomSettingsSubmitData {
    const diseaseMatrix = values.diseasesPerRole[values.roleId];
    const activeRoleBucket =
      optimize === 'full' && rolesAndPresets && rolesAndPresets.find(x => x.id === values.roleId);
    return {
      roleId: values.roleId,
      locations: values.locations,
      diseaseMatrix: diseaseMatrix,
      ...(optimize === 'full' && {
        isPresetAltered: ComputeIsDiseasematrixAltered(
          activeRoleBucket && activeRoleBucket.preset,
          diseaseMatrix
        ),
      }),
    };
  }

  const formikSeedValue = useMemo<CustomSettingsFM>(
    () => ({
      roleId: userRoleIdInitial,
      locations:
        geonames &&
        geonames.map(
          x =>
            ({
              locationId: x.geonameId,
              name: x.fullDisplayName,
            } as LocationSelectionItem)
        ),
      diseasesPerRole: {
        ...mapToDictionary(
          rolesAndPresets,
          x => x.id,
          x => ({ ...x.preset })
        ),
        ...(userRoleIdInitial &&
          userDiseaseRelevanceSetting && {
            [userRoleIdInitial]: { ...userDiseaseRelevanceSetting },
          }),
      },
    }),
    [userRoleIdInitial, geonames, rolesAndPresets, userDiseaseRelevanceSetting]
  );

  const formikSeedValueJson = useMemo(
    () => JSON.stringify(Map2SubmitData(formikSeedValue, 'quick')),
    [formikSeedValue]
  );

  const roleOptions = useMemo<DropdownItemProps[]>(
    () =>
      rolesAndPresets &&
      rolesAndPresets.map(x => ({
        text: x.name || 'Select a role',
        value: x.id,
      })),
    [rolesAndPresets]
  );

  const scrollToCustomizedDiseases = () => {
    if (
      refCustomDiseasesButtonDiv &&
      refCustomDiseasesButtonDiv.current &&
      refCustomDiseasesButtonDiv.current.scrollIntoView
    ) {
      setIsCustomizeDiseasesOpen(true);
      setTimeout(() => {
        refCustomDiseasesButtonDiv.current.scrollIntoView();
      }, 10);
    }
  };

  const handleFormSubmit = useCallback(
    (values: CustomSettingsFM, formikHelpers: FormikHelpers<CustomSettingsFM>) => {
      const { setSubmitting } = formikHelpers;
      const submitData = Map2SubmitData(values, 'full');
      onSubmit && onSubmit(submitData, setSubmitting);
    },
    [onSubmit, rolesAndPresets]
  );

  const ComputeIsDiseasematrixAltered = (
    matrix1: DiseaseNotificationLevelDict,
    matrix2: DiseaseNotificationLevelDict
  ) => (matrix1 && matrix2 && JSON.stringify(matrix1) !== JSON.stringify(matrix2)) || false;

  if (!rolesAndPresets || !diseases || !diseaseGroupings) {
    return <div />;
  }

  return (
    <Formik<CustomSettingsFM>
      enableReinitialize
      initialValues={formikSeedValue}
      onSubmit={handleFormSubmit}
      validationSchema={Yup.object().shape<Partial<CustomSettingsFM>>({
        locations: Yup.array<LocationSelectionItem>()
          .required('Please enter at least one location')
          .max(maxAois, obj => {
            const excessLocationCount = obj.value.length - maxAois;
            const strMaxLocations = formatNumber(maxAois, 'location');
            const strExcessLocations = formatNumber(excessLocationCount, 'location');
            return `You’ve selected over ${strMaxLocations}. Please remove ${strExcessLocations} to continue.`;
          }),
      })}
    >
      {formikProps => {
        const { values, errors, touched, getFieldHelpers, handleSubmit, isSubmitting } =
          formikProps;
        const hasErrors = hasIntersection(Object.keys(errors), Object.keys(touched));
        const hasLocationErrors = touched.locations && !!errors.locations;
        const activeRoleBucket =
          rolesAndPresets && rolesAndPresets.find(x => x.id === values.roleId);
        const resetFormikToSelectedRolePreset = () => {
          const { setValue } = getFieldHelpers(
            `${nameof<CustomSettingsFM>('diseasesPerRole')}.${values.roleId}`
          );
          setValue({ ...activeRoleBucket.preset });
          // scrollToCustomizedDiseases();
        };
        const isDiseasesAltered =
          activeRoleBucket &&
          ComputeIsDiseasematrixAltered(
            activeRoleBucket.preset,
            values.diseasesPerRole[values.roleId]
          );
        const isFormAltered =
          formikSeedValueJson !== JSON.stringify(Map2SubmitData(values, 'quick'));
        return (
          <form onSubmit={handleSubmit}>
            <PageHeading>My Locations</PageHeading>
            <div sx={{ pb: '16px' }}>
              <LocationMultiSelectFormikControl
                name={nameof<CustomSettingsFM>('locations')}
                error={hasLocationErrors}
                errorMessage={hasLocationErrors ? errors.locations.toString() : null}
              />
            </div>
            <PageHeading>My Role</PageHeading>
            <PageParagraph>
              See disease risks most likely to affect you within the Custom View on your dashboard.
              You will also receive notifications about these diseases.
            </PageParagraph>
            <PageParagraph>
              You can further customize your disease list below by clicking on "Customize Diseases."
            </PageParagraph>
            <PageParagraph>
              <FlexGroup prefix="I work in">
                <FormikSemanticDropDown
                  fluid
                  name={nameof<CustomSettingsFM>('roleId')}
                  placeholder="Select a role"
                  options={roleOptions}
                  error={touched.roleId && !!errors.roleId}
                />
              </FlexGroup>
            </PageParagraph>
            {activeRoleBucket && activeRoleBucket.notificationDescription && (
              <PageParagraph>
                <span
                  dangerouslySetInnerHTML={{ __html: activeRoleBucket.notificationDescription }}
                />
              </PageParagraph>
            )}
            {isDiseasesAltered && (
              <PageParagraph>
                Your disease list has been customized —{' '}
                <span
                  onClick={scrollToCustomizedDiseases}
                  sx={{
                    color: 'black',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Edit customized diseases
                </span>
              </PageParagraph>
            )}
            <PageParagraph>
              <FlexGroup
                stackable
                suffix={
                  isDiseasesAltered &&
                  activeRoleBucket && (
                    <span onClick={resetFormikToSelectedRolePreset} sx={{ cursor: 'pointer' }}>
                      {`Reset to ${activeRoleBucket.name} presets`}
                    </span>
                  )
                }
              >
                <Typography variant="h5" color="stone90">
                  Customize Diseases
                </Typography>
              </FlexGroup>
            </PageParagraph>
            {isMobileDevice && (
              <PageParagraph>
                <Typography variant="body1" color="stone90">
                  To edit your customized diseases, visit your insights settings on your desktop.
                </Typography>
              </PageParagraph>
            )}
            {isNonMobileDevice && (
              <>
                <div ref={refCustomDiseasesButtonDiv}>
                  <Button
                    type="button" // LESSON: form will submit on ANY button press unless its marked with type="button" (LINK: https://github.com/jaredpalmer/formik/issues/1610#issuecomment-502831705)
                    onClick={() => setIsCustomizeDiseasesOpen(!isCustomizeDiseasesOpen)}
                    sx={{
                      '&.ui.button': {
                        width: '100%',
                        background: 'none',
                        border: '1px solid #364E78',
                        fontSize: '16px',
                        lineHeight: '19px',
                        borderRadius: '3px',
                        padding: '10px',
                        cursor: 'pointer',
                        ...sxMixinActiveHover(),
                      },
                    }}
                  >
                    <Typography variant="button" color="stone90">
                      {isCustomizeDiseasesOpen
                        ? 'Hide Customized Diseases'
                        : 'Customize My Diseases'}
                    </Typography>
                  </Button>
                </div>
                {isCustomizeDiseasesOpen && (
                  <Grid>
                    <Grid.Column width={16}>
                      <DiseaseRelevanceMatrixFormikControl
                        formikProps={formikProps}
                        diseaseGroupings={diseaseGroupings}
                        diseases={diseases}
                      />
                    </Grid.Column>
                    {/* <Grid.Column width={4}>
                    <pre sx={{ fontSize: '8px' }}>{JSON.stringify(values, null, 2)}</pre>
                  </Grid.Column> */}
                  </Grid>
                )}
              </>
            )}
            <div sx={{ display: 'flex', justifyContent: 'center', mt: '2em' }}>
              <SettingsSubmitButton
                disabled={!isFormAltered || hasErrors || isSubmitting}
                text="Update Settings"
              />
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
